import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Mail, MapPin, Phone, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useI18n } from '../context/I18nContext';
import { publicApi } from '../api';
import { staticImg } from '../utils/media';

export default function Footer() {
  const { toast } = useToast();
  const { meta, tr } = useI18n();
  const [email, setEmail] = useState('');
  const settings = meta?.settings || {};
  const menus = meta?.menus || [];
  const roots = menus.filter((m) => !m.parent_id);
  const kidsOf = (id) => menus.filter((m) => m.parent_id === id);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await publicApi.newsletter(email);
      toast({ title: tr('newsletter.ok_title', 'Bültene abone olundu'), description: tr('newsletter.ok_desc', 'Kampanyalarımız gönderilecek.') });
      setEmail('');
    } catch {
      toast({ title: tr('newsletter.err_title', 'Hata'), description: tr('newsletter.err_desc', 'Bültene abonelik sırasında hata oluştu.'), variant: 'destructive' });
    }
  };

  const norm = (u) => (u && u.startsWith('/') ? u : `/${u || ''}`);
  const trMenu = (title, url) => {
    if (url === '/') return tr('home.nav_home', title);
    if (url === '/products') return tr('home.nav_products', title);
    if (url === '/updates') return tr('home.nav_updates', title);
    if (url === '/blog') return tr('home.nav_blog', title);
    if (url === '/contact') return tr('home.nav_contact', title);
    return title;
  };
  const productsRoot = roots.find((m) => (m.title || '').toLowerCase().includes('ürün') || (m.title || '').toLowerCase().includes('product'));
  const serversRoot = roots.find((m) => (m.title || '').toLowerCase().includes('sunuc') || (m.title || '').toLowerCase().includes('server'));
  const otherRoot = roots.find((m) => (m.title || '').toLowerCase().includes('diğer') || (m.title || '').toLowerCase().includes('other'));

  const cols = [
    { title: tr('footer.products', 'ÜRÜNLER').toUpperCase(), links: productsRoot ? kidsOf(productsRoot.id) : [] },
    { title: tr('footer.servers', 'SUNUCULAR').toUpperCase(), links: serversRoot ? kidsOf(serversRoot.id) : [] },
    { title: tr('footer.corporate', 'KURUMSAL').toUpperCase(), links: otherRoot ? kidsOf(otherRoot.id) : [] },
    { title: tr('footer.legal', 'YASAL').toUpperCase(), links: [
      { title: tr('footer.privacy', 'Gizlilik Politikası'), url: '/pages/privacy' },
      { title: tr('footer.terms', 'Kullanıcı Sözleşmesi'), url: '/pages/terms' },
      { title: tr('footer.kvkk', 'KVKK Aydınlatma Metni'), url: '/pages/kvkk' },
      { title: tr('footer.refund', 'İade & İptal Koşulları'), url: '/pages/refund' },
      { title: tr('footer.mobile_privacy', 'Mobil Uygulama Gizlilik'), url: '/pages/mobile-privacy' },
    ] },
  ];

  return (
    <footer className="relative pt-16 md:pt-20 pb-8 border-t border-[#e4b44e]/15 bg-[#07070a]">
      <div className="absolute inset-x-0 top-0 h-40 glow-radial pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10 relative">
        <div className="rounded-3xl border border-[#e4b44e]/25 bg-gradient-to-r from-[#141110] via-[#0d0c10] to-[#141110] p-6 md:p-12 mb-14 md:mb-16 grid md:grid-cols-2 gap-6 md:gap-8 items-center">
          <div>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-black text-white mb-2">{tr('footer.nl_title', 'Bültenimize')} <span className="gold-text">{tr('footer.nl_title2', 'Abone Olun')}</span></h3>
            <p className="text-white/60 text-sm md:text-base">{tr('footer.nl_desc', 'Yeni ürünler, teknik yazılar ve özel kampanyalar doğrudan gelen kutunuza.')}</p>
          </div>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={tr('footer.email_ph', 'E-posta adresiniz')}
                className="w-full h-12 pl-11 pr-4 rounded-full bg-white/5 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-[#e4b44e]/60 transition" />
            </div>
            <button className="btn-gold h-12 px-6 rounded-full inline-flex items-center justify-center gap-2 text-xs font-extrabold tracking-widest whitespace-nowrap">
              {tr('footer.subscribe', 'ABONE OL')} <Send className="w-4 h-4" />
            </button>
          </form>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-14">
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-5">
              {settings.site_logo ? (
                <img src={staticImg(settings.site_logo)} alt="Logo" className="w-10 h-10 object-contain" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#f0c766] to-[#a97a1f] flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-[#17140b]" strokeWidth={2.4} />
                </div>
              )}
              <div className="font-display text-lg font-extrabold tracking-widest text-white">AEGIS<span className="gold-text-animated"> STUDIOS</span></div>
            </div>
            <p className="text-sm text-white/55 leading-relaxed mb-6 max-w-sm">{settings.footer_about || ''}</p>
            <div className="space-y-2 text-sm text-white/60">
              {settings.contact_email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#e4b44e]" /> {settings.contact_email}</div>}
              {(settings.contact_phone || '+90 537 463 06 44') && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#e4b44e]" /> {settings.contact_phone || '+90 537 463 06 44'}</div>}
              {(settings.contact_location || tr('footer.location', 'Londra · İstanbul')) && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#e4b44e]" /> {settings.contact_location || tr('footer.location', 'Londra · İstanbul')}</div>}
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <div className="text-xs font-bold tracking-[0.3em] text-[#e4b44e]/85 mb-4">{c.title}</div>
              <ul className="space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.title || l.url}>
                    <Link to={norm(l.url)} className="text-sm text-white/60 hover:text-[#f4d896] transition">{trMenu(l.title, norm(l.url))}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="divider-gold mb-6" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40 tracking-wide">
          <div>{settings.footer_copyright || `© ${new Date().getFullYear()} Aegis Studios`}</div>
          <div className="flex items-center gap-4">
            <span>256-Bit SSL</span><span className="w-1 h-1 rounded-full bg-white/20" /><span>{tr('footer.instant', 'Anında Teslimat')}</span><span className="w-1 h-1 rounded-full bg-white/20" /><span>7/24 {tr('footer.support', 'Destek')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
